import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LangProvider } from "@/i18n";
import { InstallApp, OfflineBanner } from "@/components/InstallApp";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { PageHeader } from "@/components/common";
import RequestsPanel from "@/components/RequestsPanel";
import UsersPanel from "@/components/UsersPanel";
import Login from "@/pages/Login";
import AcceptInvite from "@/pages/AcceptInvite";
import Dashboard from "@/pages/Dashboard";
import CompanyDetail from "@/pages/CompanyDetail";
import Compliances from "@/pages/Compliances";
import Onboarding from "@/pages/Onboarding";
import { AuthCallback, Register, VerifyEmail, ForgotPassword, ResetPassword, ConfirmEmailChange } from "@/pages/AuthPages";
import Account from "@/pages/Account";
import MyWorkspace from "@/pages/MyWorkspace";
import { AssistanceList } from "@/pages/Queue";
import Today from "@/pages/staff/Today";
import Waiting from "@/pages/staff/Waiting";
import QuoteDesk from "@/pages/staff/QuoteDesk";
import Leads from "@/pages/staff/Leads";
import Finance, { PettyCash } from "@/pages/staff/Finance";
import { TasksPanel, ClientsPanel, LibraryPanel, AdminPanel, PaymentsPanel } from "@/pages/staff/Panels";
import { Go } from "@/components/staff/Panel";
import BillingPrint from "@/pages/BillingPrint";
import ClientCalendar from "@/pages/client/ClientCalendar";
import ClientBilling from "@/pages/client/ClientBilling";
import ClientDocuments from "@/pages/client/ClientDocuments";
import ClientTeam from "@/pages/client/ClientTeam";
import ClientRequest from "@/pages/client/ClientRequest";

const ByRole = ({ staff, client }) => (useAuth().isStaff ? staff : client);

const TasksOrRegister = () => (useAuth().isStaff ? <TasksPanel /> : <Compliances />);

const BillingGate = ({ children }) => {
  const { user } = useAuth();
  return user && !(user.role === "admin" || user.perms?.[["admin", "staff"].includes(user.role) ? "billing_admin" : "billing_view"]) ? <Navigate to="/" replace /> : children;
};

const Protected = ({ staff, children }) => {
  const { user, isStaff } = useAuth();
  if (user === null) return <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground" data-testid="auth-loading">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboarding) return <Onboarding />;
  if (staff && !isStaff) return <Navigate to="/" replace />;
  return children;
};

const Simple = ({ title, subtitle, children }) => <div><PageHeader title={title} subtitle={subtitle} />{children}</div>;

const PermGate = ({ perm, to = "/", children }) => {
  const { user, isStaff } = useAuth();
  return user && !isStaff && !user.perms?.[perm] ? <Navigate to={to} replace /> : children;
};
const requestsPage = <Simple title="Service requests" subtitle="Ask V.A for filings, registrations and advice"><RequestsPanel /><h2 className="mb-3 mt-10 font-heading text-lg font-bold">V.A assistance requests</h2><AssistanceList /></Simple>;

function AppRoutes() {
  const { hash } = useLocation();
  if (hash?.includes("session_id=")) return <AuthCallback />;
  return (
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route path="/billing/invoice/:id" element={<Protected><BillingGate><BillingPrint kind="invoice" /></BillingGate></Protected>} />
          <Route path="/billing/receipt/:id" element={<Protected><BillingGate><BillingPrint kind="receipt" /></BillingGate></Protected>} />
          <Route path="/billing/summary/:tid/:month" element={<Protected><BillingGate><BillingPrint kind="summary" /></BillingGate></Protected>} />
          <Route element={<Protected><Shell /></Protected>}>
            <Route path="billing" element={<BillingGate><ByRole staff={<PaymentsPanel />} client={<ClientBilling />} /></BillingGate>} />
            <Route index element={<ByRole staff={<Today />} client={<Dashboard />} />} />
            <Route path="tasks" element={<TasksOrRegister />} />
            <Route path="waiting" element={<Protected staff><Waiting /></Protected>} />
            <Route path="quotes" element={<Protected staff><QuoteDesk /></Protected>} />
            <Route path="leads" element={<Protected staff><Leads /></Protected>} />
            <Route path="finance" element={<Protected staff><Finance /></Protected>} />
            <Route path="petty-cash" element={<Protected staff><PettyCash /></Protected>} />
            <Route path="clients" element={<Protected staff><ClientsPanel /></Protected>} />
            <Route path="library" element={<Protected staff><LibraryPanel /></Protected>} />
            <Route path="admin" element={<Protected staff><AdminPanel /></Protected>} />
            <Route path="authorities" element={<Protected staff><Go to="/library" tab="auth" /></Protected>} />
            <Route path="registrations" element={<Protected staff><Go to="/clients" tab="onb" /></Protected>} />
            <Route path="holidays" element={<Protected staff><Go to="/library" tab="hol" /></Protected>} />
            <Route path="calendar" element={<ByRole staff={<Go to="/tasks" tab="cal" />} client={<ClientCalendar />} />} />
            <Route path="documents" element={<ByRole staff={<Go to="/clients" tab="vault" />} client={<ClientDocuments />} />} />
            <Route path="requests" element={<ByRole staff={<Go to="/quotes" tab="reqs" />} client={requestsPage} />} />
            <Route path="requests/new" element={<PermGate perm="requests" to="/requests"><ByRole staff={<Go to="/quotes" tab="reqs" />} client={<ClientRequest />} /></PermGate>} />
            <Route path="company" element={<CompanyDetail />} />
            <Route path="team" element={<PermGate perm="team_manage"><ByRole staff={<Simple title="Team" subtitle="People in your company who can use FILIT Compliance Hub"><UsersPanel /></Simple>} client={<ClientTeam />} /></PermGate>} />
            <Route path="account" element={<Account />} />
            <Route path="me" element={<Protected staff><MyWorkspace /></Protected>} />
            <Route path="performance" element={<Protected staff><Navigate to="/me" replace /></Protected>} />
            <Route path="companies" element={<Protected staff><Go to="/clients" tab="cos" /></Protected>} />
            <Route path="companies/:id" element={<Protected staff><CompanyDetail /></Protected>} />
            <Route path="templates" element={<Protected staff><Go to="/library" tab="tpl" /></Protected>} />
            <Route path="users" element={<Protected staff><Go to="/admin" tab="users" /></Protected>} />
            <Route path="audit" element={<Protected staff><Go to="/admin" tab="audit" /></Protected>} />
            <Route path="queue" element={<Protected staff><Go to="/quotes" /></Protected>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <LangProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      <OfflineBanner />
      <InstallApp />
      </LangProvider>
    </AuthProvider>
  );
}

export default App;
